import React, { useState } from 'react';
import { TrendingUp, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(parseFloat(v) || 0);

const fmtFull = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

function Metric({ label, value, hint, variant }) {
  const colors = {
    green: 'text-emerald-700',
    blue: 'text-primary',
    purple: 'text-violet-700',
    default: 'text-foreground',
  };
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-base font-bold ${colors[variant] || colors.default}`} data-role="mono">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function computeEconomics(project, includeCommission = false) {
  const rb = project?.revenue_breakdown || {};
  const eb = project?.expense_breakdown || {};
  const sc = project?.sales_commission || {};

  const oneTimeRev = parseFloat(rb.one_time_charge) || 0;
  const monthlyRev = parseFloat(rb.monthly_recurring) || 0;
  const annualRev = parseFloat(rb.annual_recurring) || 0;

  const laborCost = parseFloat(eb.labor_cost) || 0;
  const laborAdj = parseFloat(eb.labor_adjustment) || 0;
  const toolCosts = parseFloat(eb.tool_costs) || 0;
  const miscCosts = parseFloat(eb.misc_costs) || 0;
  const monthlyCostRecurring = parseFloat(eb.monthly_recurring) || 0;
  const annualCostRecurring = parseFloat(eb.annual_recurring) || 0;

  // Commission amounts
  const commOneTime = parseFloat(sc.one_time_amount) || 0;
  const commRecurringAnnual = parseFloat(sc.recurring_amount) || 0;
  const commRecurringMonthly = commRecurringAnnual / 12;
  const active = includeCommission;

  // Net revenue (after commission if active)
  const netOneTimeRev = active ? oneTimeRev - commOneTime : oneTimeRev;
  const netMonthlyRev = active ? monthlyRev - (commRecurringMonthly * monthlyRev / ((monthlyRev * 12 + annualRev) || 1) * 12 / 12) : monthlyRev;
  // Simpler: proportional split of recurring commission across monthly and annual
  const recurringRevTotal = (monthlyRev * 12) + annualRev;
  const monthlyShare = recurringRevTotal > 0 ? (monthlyRev * 12) / recurringRevTotal : 0;
  const annualShare = recurringRevTotal > 0 ? annualRev / recurringRevTotal : 0;
  const commOnMonthly = commRecurringAnnual * monthlyShare; // annual commission attributed to monthly
  const commOnAnnual = commRecurringAnnual * annualShare;   // annual commission attributed to annual
  const netMonthlyRevPerMonth = active ? monthlyRev - (commOnMonthly / 12) : monthlyRev;
  const netAnnualRev = active ? annualRev - commOnAnnual : annualRev;

  // Key metrics
  const cashThisMonth = (active ? netOneTimeRev : oneTimeRev) + (active ? netMonthlyRevPerMonth : monthlyRev);
  const acv = (active ? netOneTimeRev : oneTimeRev) + ((active ? netMonthlyRevPerMonth : monthlyRev) * 12) + (active ? netAnnualRev : annualRev);
  const ltv = (active ? netOneTimeRev : oneTimeRev) + ((active ? netMonthlyRevPerMonth : monthlyRev) * 36) + ((active ? netAnnualRev : annualRev) * 3);

  // Setup costs (add one-time commission if active)
  const setupCosts = laborCost + laborAdj + toolCosts + miscCosts + (active ? commOneTime : 0);
  const setupRecoveryPct = setupCosts > 0 ? (oneTimeRev / setupCosts) * 100 : 0;
  const netSetupCost = setupCosts - oneTimeRev;

  // Monthly recurring profit (subtract recurring commission if active)
  const monthlyRecurringProfit = (monthlyRev + (annualRev / 12))
    - monthlyCostRecurring - (annualCostRecurring / 12)
    - (active ? (commRecurringAnnual / 12) : 0);

  // Payback period
  let paybackMonths = null;
  if (netSetupCost > 0 && monthlyRecurringProfit > 0) {
    paybackMonths = Math.ceil(netSetupCost / monthlyRecurringProfit);
  } else if (netSetupCost <= 0) {
    paybackMonths = 0;
  }

  return {
    cashThisMonth, acv, ltv,
    setupCosts, setupRecoveryPct, netSetupCost,
    monthlyRecurringProfit, paybackMonths,
    // Gross values (always original)
    oneTimeRev, monthlyRev, annualRev,
    // Net display values
    netOneTimeRev: active ? netOneTimeRev : oneTimeRev,
    netMonthlyRev: active ? netMonthlyRevPerMonth : monthlyRev,
    netAnnualRev: active ? netAnnualRev : annualRev,
    // Commission
    commOneTime, commRecurringAnnual,
    commTotal: commOneTime + commRecurringAnnual,
    hasCommission: (commOneTime + commRecurringAnnual) > 0,
  };
}

export default function EconomicsModal({ open, onOpenChange, project }) {
  const [includeComm, setIncludeComm] = useState(false);

  if (!project) return null;

  const sc = project.sales_commission || {};
  const hasCommData = (parseFloat(sc.one_time_amount) || 0) + (parseFloat(sc.recurring_amount) || 0) > 0;
  const e = computeEconomics(project, includeComm);
  const netLabel = includeComm ? ' (Net)' : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2" data-role="heading">
              <TrendingUp className="h-5 w-5 text-primary" />
              Unit Economics
            </DialogTitle>
            {hasCommData && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Include Commission</span>
                <Switch checked={includeComm} onCheckedChange={setIncludeComm} />
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{project.project_name}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5">
          {/* Top metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className={`border rounded-lg p-3 text-center ${includeComm ? 'border-amber-200 bg-amber-50/20' : ''}`}>
              <Metric label={`Cash This Month${netLabel}`} value={fmt(e.cashThisMonth)} variant="green" hint="One-time + first monthly" />
            </div>
            <div className={`border rounded-lg p-3 text-center ${includeComm ? 'border-amber-200 bg-amber-50/20' : ''}`}>
              <Metric label={`Annual Value${netLabel}`} value={fmt(e.acv)} variant="blue" hint="First-year total" />
            </div>
            <div className={`border rounded-lg p-3 text-center ${includeComm ? 'border-amber-200 bg-amber-50/20' : ''}`}>
              <Metric label={`Lifetime Value${netLabel}`} value={fmt(e.ltv)} variant="purple" hint="36-month projection" />
            </div>
          </div>

          <Separator />

          {/* Payback & Recovery */}
          <div className="space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
              Payback & Recovery
              {includeComm && <span className="text-amber-600 normal-case font-normal ml-1">(net of commission)</span>}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className={`border rounded-lg p-3 ${includeComm ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Setup Cost Recovery</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xl font-bold ${e.setupRecoveryPct >= 100 ? 'text-emerald-700' : 'text-amber-600'}`} data-role="mono">
                    {e.setupRecoveryPct.toFixed(0)}%
                  </span>
                  {e.setupRecoveryPct >= 100 && <ArrowUpRight className="h-4 w-4 text-emerald-500" />}
                  {e.setupRecoveryPct < 100 && e.setupRecoveryPct > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {fmtFull(e.oneTimeRev)} rev / {fmtFull(e.setupCosts)} costs
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {e.setupRecoveryPct >= 100 ? 'Recovered on day 1' : `${fmtFull(e.netSetupCost)} deficit`}
                </p>
              </div>

              <div className={`border rounded-lg p-3 ${includeComm ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Payback Period</p>
                <span className={`text-xl font-bold ${e.paybackMonths === 0 ? 'text-emerald-700' : e.paybackMonths !== null && e.paybackMonths <= 6 ? 'text-blue-600' : 'text-amber-600'}`} data-role="mono">
                  {e.paybackMonths === 0 ? 'Instant' : e.paybackMonths !== null ? `${e.paybackMonths} mo` : 'N/A'}
                </span>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Monthly profit: {fmtFull(e.monthlyRecurringProfit)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {e.paybackMonths === 0 ? 'Fully covered by one-time rev' : e.paybackMonths !== null ? `${e.paybackMonths}mo to cover ${fmtFull(e.netSetupCost)}` : 'No recurring profit'}
                </p>
              </div>
            </div>
          </div>

          {/* Commission impact */}
          {includeComm && hasCommData && (
            <>
              <Separator />
              <div className="bg-amber-50/50 border border-amber-200/60 rounded-lg p-3 space-y-1 text-xs">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">Commission Deducted</h3>
                <div className="flex justify-between"><span className="text-muted-foreground">One-Time Commission</span><span className="font-semibold" data-role="mono">-{fmtFull(e.commOneTime)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Recurring Commission (Annual)</span><span className="font-semibold" data-role="mono">-{fmtFull(e.commRecurringAnnual)}</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="font-medium">Total Commission</span><span className="font-bold text-amber-700" data-role="mono">-{fmtFull(e.commTotal)}</span></div>
              </div>
            </>
          )}

          <Separator />

          {/* Revenue components */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
              Revenue Components{includeComm ? ' (Net)' : ''}
            </h3>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className={`rounded px-2.5 py-2 ${includeComm ? 'bg-amber-50/50 border border-amber-200/40' : 'bg-muted/50'}`}>
                <span className="text-muted-foreground block">One-Time{includeComm ? ' (Net)' : ''}</span>
                <span className="font-semibold" data-role="mono">{fmtFull(e.netOneTimeRev)}</span>
                {includeComm && e.commOneTime > 0 && (
                  <span className="text-[9px] text-amber-600 block">was {fmtFull(e.oneTimeRev)}</span>
                )}
              </div>
              <div className={`rounded px-2.5 py-2 ${includeComm ? 'bg-amber-50/50 border border-amber-200/40' : 'bg-muted/50'}`}>
                <span className="text-muted-foreground block">Monthly{includeComm ? ' (Net)' : ''}</span>
                <span className="font-semibold" data-role="mono">{fmtFull(e.netMonthlyRev)}/mo</span>
                {includeComm && e.commRecurringAnnual > 0 && (
                  <span className="text-[9px] text-amber-600 block">was {fmtFull(e.monthlyRev)}/mo</span>
                )}
              </div>
              <div className={`rounded px-2.5 py-2 ${includeComm ? 'bg-amber-50/50 border border-amber-200/40' : 'bg-muted/50'}`}>
                <span className="text-muted-foreground block">Annual{includeComm ? ' (Net)' : ''}</span>
                <span className="font-semibold" data-role="mono">{fmtFull(e.netAnnualRev)}/yr</span>
                {includeComm && e.commRecurringAnnual > 0 && (
                  <span className="text-[9px] text-amber-600 block">was {fmtFull(e.annualRev)}/yr</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
